import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import * as qtyApi from '../../api/app/plannedQtyApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Loader } from '../../components/core/Loader/Loader';
import { AssetDashboardSetting } from '../../types/app/asset.types';
import {
  PlannedQty,
  PlannedQtyCreateRequest,
  PlannedQtyUpdateRequest,
} from '../../types/app/plannedQty.types';
import { PlannedQtyListDataProcess } from '.';
import { SubmitHandler } from 'react-hook-form';
import {
  convertDataFromInputToCreateRequest,
  convertDataFromInputToUpdateRequest,
} from '../../utils/core/functions/plannedQty.functions';
import moment from 'moment';
import { hoursMinutesSecondsShowFormat } from '../../types/core/date.types';
import { Timezone } from '../../components/app/TimezoneChanger/TimezoneChanger';
import { useTranslation } from '../../contexts/translation';

interface Props {
  settingData: AssetDashboardSetting;
  timezone: Timezone;
  readOnly?: boolean;
}

export function PlannedQtyList({ settingData, timezone, readOnly }: Props) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();

  // Načteme plán
  const { data, isLoading, isError } = useQuery<PlannedQty[]>(['plannedQty', id], () =>
    qtyApi.getPlannedQtyList(Number(id)),
  );
  // Muatate apis
  const { mutate: mutateDeletePlannedQty } = useMutation(qtyApi.deletePlannedQty);
  const { mutate: mutateCreatePlannedQty } = useMutation(qtyApi.createPlannedQty);
  const { mutate: mutateUpdatePlannedQty } = useMutation(qtyApi.updatePlannedQty);

  // Založení nového plánu
  const handleOnSubmitCreate: SubmitHandler<PlannedQtyCreateRequest> = (
    data: PlannedQtyCreateRequest,
  ) => {
    const requestData = convertDataFromInputToCreateRequest({
      ...data,
      time: moment(data.time.utc(), hoursMinutesSecondsShowFormat).format(
        hoursMinutesSecondsShowFormat,
      ),
    });
    mutateCreatePlannedQty(
      { ...requestData },
      {
        onSuccess: () => {
          enqueueSnackbar(t('page.dashboard.planned-qty.item-created'), {
            variant: 'success',
          });
          queryClient.invalidateQueries(['plannedQty']);
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  // Editace existujícího plánu
  const handleOnSubmitUpdate: SubmitHandler<PlannedQtyUpdateRequest> = (
    data: PlannedQtyUpdateRequest,
  ) => {
    const requestData = convertDataFromInputToUpdateRequest({
      ...data,
      time: moment(data.time.utc(), hoursMinutesSecondsShowFormat).format(
        hoursMinutesSecondsShowFormat,
      ),
      id: data.id,
    });

    mutateUpdatePlannedQty(
      { ...requestData, id: requestData.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('page.dashboard.planned-qty.item-edited'), {
            variant: 'success',
          });
          queryClient.invalidateQueries(['plannedQty']);
        },
        onError: (result) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  // Smazání plánu
  const handleOnDelete = (id: number) => {
    mutateDeletePlannedQty(id, {
      onSuccess: () => {
        enqueueSnackbar(t('page.dashboard.planned-qty.item-deleted'), { variant: 'success' });
        queryClient.invalidateQueries(['plannedQty']);
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data) return <Loader />;

  return (
    <PlannedQtyListDataProcess
      data={data}
      timezone={timezone}
      settingData={settingData}
      onCreateClick={handleOnSubmitCreate}
      onEditClick={handleOnSubmitUpdate}
      onDeleteClick={handleOnDelete}
      readOnly={readOnly}
      mutateCreatePlannedQty={mutateCreatePlannedQty}
      mutateUpdatePlannedQty={mutateUpdatePlannedQty}
      mutateDeletePlannedQty={mutateDeletePlannedQty}
    />
  );
}
